import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import DeleteIcon from '@mui/icons-material/Delete';
import HomeIcon from '@mui/icons-material/Home';
import {
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import React, { useRef, useState } from 'react';
import { useDeleteGalleryPhoto } from '../../../../hooks/sitter/useDeleteGalleryPhoto';
import { useUploadGalleryPhoto } from '../../../../hooks/sitter/useUploadGalleryPhoto';

export interface HomeData {
  type: string;
  size: string;
  fencedYard: boolean;
  hasPets: boolean;
  petsDescription: string;
  hasChildren: boolean;
  childrenAges: string;
  smoking: boolean;
  photos: string[];
}

interface YourHomeSectionProps {
  data: HomeData;
  onSave: (data: HomeData) => void;
}

const switchSx = {
  '& .MuiSwitch-switchBase.Mui-checked': { color: '#178B8E' },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#178B8E' },
};

const HOME_TYPES = ['house', 'apartment', 'condo', 'other'];
const HOME_SIZES = ['studio', '1BR', '2BR', '3BR', '4BR+'];

const YourHomeSection: React.FC<YourHomeSectionProps> = ({ data, onSave }) => {
  const [draft, setDraft] = useState<HomeData>(data);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { mutate: uploadGalleryPhoto, isPending: isUploading } = useUploadGalleryPhoto();
  const { mutate: deleteGalleryPhoto } = useDeleteGalleryPhoto();

  const handleField = <K extends keyof HomeData>(field: K, value: HomeData[K]) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
  };

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    const remaining = 5 - draft.photos.length;
    const filesToUpload = files.slice(0, remaining);

    filesToUpload.forEach((file) => {
      // Show blob preview immediately
      const previewUrl = URL.createObjectURL(file);
      setDraft((prev) => ({ ...prev, photos: [...prev.photos, previewUrl] }));

      // Upload and replace preview with real URL
      uploadGalleryPhoto(file, {
        onSuccess: (realUrl) => {
          setDraft((prev) => ({
            ...prev,
            photos: prev.photos.map((p) => (p === previewUrl ? realUrl : p)),
          }));
        },
      });
    });

    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDeletePhoto = (index: number) => {
    const photoUrl = draft.photos[index];
    setDraft((prev) => ({ ...prev, photos: prev.photos.filter((_, i) => i !== index) }));
    deleteGalleryPhoto(photoUrl);
  };

  return (
    <Paper sx={{ p: 3, borderRadius: 2, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <HomeIcon sx={{ color: '#178B8E', fontSize: 22 }} />
        <Typography variant="h6" fontWeight={600}>
          Your Home
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Help owners know what to expect when their pet stays with you
      </Typography>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            select
            fullWidth
            label="Home type"
            value={draft.type}
            onChange={(e) => handleField('type', e.target.value)}
            size="small"
          >
            {HOME_TYPES.map((t) => (
              <MenuItem key={t} value={t} sx={{ textTransform: 'capitalize' }}>
                <Typography sx={{ textTransform: 'capitalize' }}>{t}</Typography>
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            select
            fullWidth
            label="Home size"
            value={draft.size}
            onChange={(e) => handleField('size', e.target.value)}
            size="small"
          >
            {HOME_SIZES.map((s) => (
              <MenuItem key={s} value={s}>
                {s === 'studio' ? 'Studio' : s}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

      <Divider sx={{ my: 2.5 }} />

      {/* Switches */}
      <Grid container spacing={0}>
        {/* Fenced yard */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              py: 1,
              pr: { sm: 2 },
            }}
          >
            <Typography variant="body2">Fenced yard</Typography>
            <Switch
              checked={draft.fencedYard}
              onChange={() => handleField('fencedYard', !draft.fencedYard)}
              size="small"
              sx={switchSx}
            />
          </Box>
        </Grid>

        {/* Smoking */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              py: 1,
              pr: { sm: 2 },
            }}
          >
            <Typography variant="body2">Smoking household</Typography>
            <Switch
              checked={draft.smoking}
              onChange={() => handleField('smoking', !draft.smoking)}
              size="small"
              sx={switchSx}
            />
          </Box>
        </Grid>

        {/* Other pets */}
        <Grid size={{ xs: 12 }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1 }}
          >
            <Typography variant="body2">I have other pets at home</Typography>
            <Switch
              checked={draft.hasPets}
              onChange={() => handleField('hasPets', !draft.hasPets)}
              size="small"
              sx={switchSx}
            />
          </Box>
          {draft.hasPets && (
            <TextField
              fullWidth
              size="small"
              placeholder="Describe your pets (e.g. 1 friendly golden retriever)"
              value={draft.petsDescription}
              onChange={(e) => handleField('petsDescription', e.target.value)}
              sx={{ mb: 1 }}
            />
          )}
        </Grid>

        {/* Children */}
        <Grid size={{ xs: 12 }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1 }}
          >
            <Typography variant="body2">Children live in my home</Typography>
            <Switch
              checked={draft.hasChildren}
              onChange={() => handleField('hasChildren', !draft.hasChildren)}
              size="small"
              sx={switchSx}
            />
          </Box>
          {draft.hasChildren && (
            <TextField
              fullWidth
              size="small"
              placeholder="Children's ages (e.g. 6 and 9)"
              value={draft.childrenAges}
              onChange={(e) => handleField('childrenAges', e.target.value)}
              sx={{ mb: 1 }}
            />
          )}
        </Grid>
      </Grid>

      <Divider sx={{ my: 2.5 }} />

      {/* Photos */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Box>
          <Typography variant="body1" fontWeight={600}>
            Home Photos
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Up to 5 photos — helps owners feel confident
          </Typography>
        </Box>
        {draft.photos.length < 5 && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddPhotoAlternateIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            sx={{ textTransform: 'none', borderColor: '#178B8E', color: '#178B8E' }}
          >
            {isUploading ? 'Uploading...' : 'Add Photo'}
          </Button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={handlePhotoUpload}
        />
      </Box>

      {draft.photos.length > 0 ? (
        <Grid container spacing={1.5}>
          {draft.photos.map((src, i) => (
            <Grid size={{ xs: 6, sm: 4, md: 3 }} key={i}>
              <Box
                sx={{
                  position: 'relative',
                  borderRadius: 1.5,
                  overflow: 'hidden',
                  aspectRatio: '4/3',
                  backgroundColor: '#F5F5F5',
                }}
              >
                <img
                  src={src}
                  alt={`Home photo ${i + 1}`}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
                <IconButton
                  size="small"
                  onClick={() => handleDeletePhoto(i)}
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    color: 'white',
                    width: 26,
                    height: 26,
                    '&:hover': { backgroundColor: 'rgba(211,47,47,0.8)' },
                  }}
                >
                  <DeleteIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box
          onClick={() => fileInputRef.current?.click()}
          sx={{
            border: '2px dashed',
            borderColor: 'divider',
            borderRadius: 2,
            p: 4,
            textAlign: 'center',
            cursor: 'pointer',
            '&:hover': { borderColor: '#178B8E', backgroundColor: 'rgba(23,139,142,0.04)' },
          }}
        >
          <AddPhotoAlternateIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            Click to upload home photos
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Button
          variant="contained"
          size="small"
          onClick={() => onSave(draft)}
          sx={{
            textTransform: 'none',
            backgroundColor: '#178B8E',
            '&:hover': { backgroundColor: '#146f72' },
          }}
        >
          Save Changes
        </Button>
      </Box>
    </Paper>
  );
};

export default YourHomeSection;
